import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may read the shift register.
 *
 * Same shape as `operationsAccess` / `accountsAccess` — the per-domain access
 * module every back-office screen carries — and the same constraint that is easy
 * to miss: bos-api's `BranchAdmin` policy requires `auth_method=password` **as
 * well as** a role. A BranchAdmin holding a PIN-issued kiosk token is a
 * BranchAdmin, and still receives 403.
 *
 * There is no `full` level here, because there is nothing to write. This screen
 * summarizes what the till already posted; it never opens, closes or amends a
 * shift, and the endpoints that do are PIN-only and unreachable from this app.
 */
export type ShiftsAccess =
    /**
     * A PIN/kiosk session. Every request would 403 whatever the role, so the
     * page must not fetch at all.
     */
    | { kind: 'kiosk' }
    /** Signed in, but the role does not permit reading (a Seller). */
    | { kind: 'forbidden' }
    /** OrgAdmin or BranchAdmin with a password session. */
    | { kind: 'read' };

/**
 * Derive the access level from the persisted session. Fails closed on every
 * unknown: no user, or an unreadable `auth_method`, is a kiosk session rather
 * than an optimistic render of a page whose every request would 403.
 */
export const deriveShiftsAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): ShiftsAccess => {
    if (!user || authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    switch (user.role) {
        case 'OrgAdmin':
        case 'BranchAdmin':
            return { kind: 'read' };
        default:
            return { kind: 'forbidden' };
    }
};

/** Whether this access level may read the register — and so, may fetch. */
export const canReadShifts = (access: ShiftsAccess): boolean =>
    access.kind === 'read';
